import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import API from '@/apiRequest';

export const useClientUsers = (clientId) => {
  const queryClient = useQueryClient();

  const useUsersList = (options = {}) => useQuery({
    queryKey: ['clients', clientId, 'users'],
    queryFn: async () => {
      const { data } = await API.get(`/clients/${clientId}/users`);
      return data;
    },
    enabled: !!clientId,
    ...options,
  });

  const useUserDetails = (userId, options = {}) => useQuery({
    queryKey: ['clients', clientId, 'users', userId],
    queryFn: async () => {
      const { data } = await API.get(`/clients/${clientId}/users/${userId}`);
      return data;
    },
    enabled: !!clientId && !!userId,
    ...options,
  });

  const useCreateUserMutation = () => useMutation({
    mutationFn: async (userData) => {
      const { data } = await API.post(`clients/${clientId}/users`, userData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'users'] });
    },
  });

  const useUpdateUserMutation = (userId) => useMutation({
    mutationFn: async (userData) => {
      const { data } = await API.put(`clients/${clientId}/users/${userId}`, userData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'users', userId] });
      queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'users'] });
    },
  });

  const useDeleteUserMutation = (userId) => useMutation({
    mutationFn: async () => {
      const { data } = await API.delete(`clients/${clientId}/users/${userId}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'users'] });
    },
  });

  const useClientFeatures = (options = {}) => useQuery({
    queryKey: ['clients', clientId, 'features'],
    queryFn: async () => {
      const { data } = await API.get(`clients/${clientId}/features`);
      return data;
    },
    enabled: !!clientId,
    ...options,
  });

  return {
    useUsersList,
    useUserDetails,
    useCreateUserMutation,
    useUpdateUserMutation,
    useDeleteUserMutation,
    useClientFeatures,
  };
};
